import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AppointmentChip from './AppointmentChip';
import {
  HOUR_START,
  HOUR_END,
  ROW_HEIGHT,
  buildHourSlots,
  sameDay,
  formatHour,
  durationMinutes,
} from './dateUtils';
import { hourRange, hourSlotOutside, dayHoursFor } from '@/lib/businessHours';

const TIME_COL_WIDTH = 64;
const HEADER_HEIGHT = 52;

const positionForAppt = (appt, rangeStart) => {
  const start = new Date(appt.start_time);
  const minutes = start.getHours() * 60 + start.getMinutes();
  const top = ((minutes - rangeStart * 60) / 60) * ROW_HEIGHT;
  const height = Math.max(20, (durationMinutes(appt) / 60) * ROW_HEIGHT - 2);
  return { top, height };
};

// Solid dim fill for out-of-hours slots (replaces the diagonal stripes,
// which read as texture noise at week scale).
const outsideBg = (t) =>
  t.palette.mode === 'dark' ? 'rgba(0,0,0,0.28)' : t.palette.action.hover;

const NowLine = ({ rangeStart, rangeEnd }) => {
  const now = new Date();
  const dec = now.getHours() + now.getMinutes() / 60;
  if (dec < rangeStart || dec > rangeEnd + 1) return null;
  const top = (dec - rangeStart) * ROW_HEIGHT;
  return (
    <Box sx={{ position: 'absolute', top, left: 0, right: 0, display: 'flex', alignItems: 'center', zIndex: 3, pointerEvents: 'none' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', ml: '-4px', flexShrink: 0 }} />
      <Box sx={{ flex: 1, height: '2px', bgcolor: 'primary.main' }} />
    </Box>
  );
};

const DayColumn = ({ day, hours, businessHours, appts, onApptClick, onSlotClick, showHeader, isToday }) => {
  const visible = useMemo(
    () => appts.filter((a) => sameDay(a.start_time, day)),
    [appts, day],
  );

  const dayHours = dayHoursFor(businessHours, day);
  const closedAllDay = Boolean(dayHours?.closed);

  return (
    <Box
      sx={{
        flex: isToday ? 1.12 : 1,
        minWidth: 0,
        borderLeft: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {showHeader && (
        <Box
          sx={{
            height: HEADER_HEIGHT,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            bgcolor: (t) => (isToday ? alpha(t.palette.primary.main, 0.06) : t.palette.background.paper),
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: isToday ? 600 : 500, color: isToday ? 'primary.main' : 'text.secondary' }}
          >
            {day.toLocaleDateString(undefined, { weekday: 'short' })}
          </Typography>
          {isToday ? (
            <Box
              sx={{
                minWidth: 24,
                height: 24,
                px: 0.5,
                borderRadius: 999,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                {day.getDate()}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" fontWeight={700}>
              {day.getDate()}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          bgcolor: (t) => (isToday ? alpha(t.palette.primary.main, 0.035) : undefined),
        }}
      >
        {hours.map((h, idx) => {
          const outside = hourSlotOutside(businessHours, day, h);
          return (
            <Box
              key={h}
              onClick={() => {
                if (!onSlotClick) return;
                const dt = new Date(day);
                dt.setHours(h, 0, 0, 0);
                onSlotClick(dt);
              }}
              title={outside ? 'Outside of normal business hours' : undefined}
              sx={{
                height: ROW_HEIGHT,
                borderBottom: idx === hours.length - 1 ? 0 : 1,
                borderColor: 'divider',
                cursor: onSlotClick ? 'pointer' : 'default',
                bgcolor: outside ? outsideBg : undefined,
                transition: 'background-color 120ms ease',
                '&:hover': onSlotClick ? { bgcolor: 'action.selected' } : undefined,
              }}
            />
          );
        })}
        {closedAllDay && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ position: 'absolute', top: 40, left: 0, right: 0, textAlign: 'center', pointerEvents: 'none' }}
          >
            Closed
          </Typography>
        )}
        {visible.map((appt) => {
          const { top, height } = positionForAppt(appt, hours[0] ?? HOUR_START);
          return (
            <Box
              key={appt.appointment_id}
              sx={{
                position: 'absolute',
                top,
                left: 3,
                right: 3,
                height,
              }}
            >
              <AppointmentChip appt={appt} onClick={onApptClick} sx={{ height: '100%' }} />
            </Box>
          );
        })}
        {isToday && <NowLine rangeStart={hours[0] ?? HOUR_START} rangeEnd={hours[hours.length - 1] ?? HOUR_END} />}
      </Box>
    </Box>
  );
};

/**
 * TimeGrid — `bare` renders without its own border/radius so it can sit
 * flush inside a parent card (see CalendarPage's merged toolbar frame).
 */
const TimeGrid = ({ days, appts, businessHours, onApptClick, onSlotClick, showHeader = true, bare = false }) => {
  let [rangeStart, rangeEnd] = hourRange(businessHours, HOUR_START, HOUR_END);
  // Out-of-hours bookings are allowed, so the grid must also cover every
  // appointment on the visible days — otherwise early chips overlap the
  // day header (and late ones fall off the bottom).
  for (const a of appts) {
    if (!days.some((d) => sameDay(a.start_time, d))) continue;
    const start = new Date(a.start_time);
    const end = new Date(a.end_time || a.start_time);
    if (!Number.isNaN(start.getTime())) rangeStart = Math.min(rangeStart, start.getHours());
    if (!Number.isNaN(end.getTime())) rangeEnd = Math.max(rangeEnd, Math.min(23, end.getHours() + 1));
  }
  const hours = buildHourSlots(rangeStart, rangeEnd);
  const todayKey = new Date();

  return (
    <Box
      sx={{
        border: bare ? 0 : 1,
        borderColor: 'divider',
        borderRadius: bare ? 0 : 1,
        bgcolor: 'background.paper',
        overflow: 'auto',
        maxHeight: '70vh',
      }}
    >
      <Box sx={{ display: 'flex', minWidth: '100%' }}>
        <Box
          sx={{
            width: TIME_COL_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {showHeader && (
            <Box
              sx={{
                height: HEADER_HEIGHT,
                borderBottom: 1,
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
              }}
            />
          )}
          {hours.map((h) => (
            <Box
              key={h}
              sx={{
                height: ROW_HEIGHT,
                display: 'flex',
                justifyContent: 'flex-end',
                pr: 1,
                pt: 0.25,
              }}
            >
              <Typography variant="caption" color="text.disabled">
                {formatHour(h)}
              </Typography>
            </Box>
          ))}
        </Box>
        {days.map((d) => (
          <DayColumn
            key={d.toISOString()}
            day={d}
            hours={hours}
            businessHours={businessHours}
            appts={appts}
            onApptClick={onApptClick}
            onSlotClick={onSlotClick}
            showHeader={showHeader}
            isToday={sameDay(d, todayKey)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TimeGrid;
